import React from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useUser } from "../contexts/UserContext";
import { useLegalGate } from "../contexts/LegalGateContext";
import BlockingPageLoader from "./ui/BlockingPageLoader";

type AuthGateProps = {
  requireAuth?: boolean;
};

function AuthGate({ requireAuth = true }: AuthGateProps) {
  const { isAuthenticated, isAuthLoading, isUserActive, isUserDataLoading } =
    useUser();
  const { status: legalGateStatus } = useLegalGate();
  const { t } = useTranslation("common");
  const location = useLocation();

  const path = location.pathname.toLowerCase();
  const publicEntryPages = ["/", "/register"];
  const isAccessPending = isAuthenticated && isUserDataLoading;
  const isDashboardRoute = path === "/dashboard";
  const isLegalAcceptanceRoute = path === "/legalacceptance";

  if (isAuthLoading || isAccessPending) {
    if (requireAuth && isDashboardRoute) {
      return <BlockingPageLoader message={t("dashboard.loadingData")} />;
    }
    return null;
  }

  if (requireAuth && !isAuthenticated) {
    return (
      <Navigate
        to="/"
        replace
        state={{ toast: { type: "info", message: t("common.signIn") } }}
      />
    );
  }

  if (requireAuth && isUserActive === false) {
    return null;
  }

  if (requireAuth && legalGateStatus === "loading" && !isLegalAcceptanceRoute) {
    if (isDashboardRoute) {
      return <BlockingPageLoader message={t("legal.gate.loadingMessage")} />;
    }
    return null;
  }

  if (requireAuth && legalGateStatus === "required" && !isLegalAcceptanceRoute) {
    return <Navigate to="/LegalAcceptance" replace />;
  }

  if (requireAuth && legalGateStatus === "accepted" && isLegalAcceptanceRoute) {
    return <Navigate to="/Dashboard" replace />;
  }

  if (
    !requireAuth &&
    isAuthenticated &&
    !isUserDataLoading &&
    isUserActive !== false &&
    publicEntryPages.includes(path)
  ) {
    return <Navigate to="/Dashboard" replace />;
  }

  return <Outlet />;
}

export default AuthGate;
